import React from 'react';
import { Box, Typography, Stack, Tabs, TabList, TabPanel, Tab, Accordion, AccordionSummary, AccordionDetails, Card } from '@mui/joy';
import CodeMirror from '@uiw/react-codemirror';
import { python } from '@codemirror/lang-python';

const FunctionDocumentation = () => (
  <Stack spacing={3}>
    {/* Tabs for different sections */}
    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
      <Tabs
        defaultValue={0}
        sx={{
          '--Tabs-gap': '1rem',
          '--joy-shadowChannel': 'var(--joy-palette-primary-500Channel)',
          '--joy-shadowRing': '0 0 0 1px var(--joy-palette-primary-500)',
        }}
      >
        <TabList>
          <Tab>Quick Guide</Tab>
          <Tab>Post-Complete Examples</Tab>
          <Tab>Scheduled Examples</Tab>
          <Tab>Context Objects</Tab>
        </TabList>
        {/* Quick Guide Tab */}
        <TabPanel value={0}>
          <Stack spacing={2}>
            <Typography level="title-sm" sx={{ color: 'primary.500', fontWeight: 'bold' }}>
              Function Types
            </Typography>
            <Box sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'primary.200' }}>
              <Typography level="body-sm" sx={{ mb: 1, fontWeight: 'bold' }}>
                Post-Complete Functions
              </Typography>
              <Typography level="body-sm" sx={{ mb: 2, color: 'neutral.700' }}>
                • Run automatically after a task is completed<br />
                • Have access to task result and payload data<br />
                • Ideal for notifications, external system updates, or data processing<br />
                • No interval required
              </Typography>
              <Typography level="body-sm" sx={{ mb: 1, fontWeight: 'bold' }}>
                Scheduled Functions
              </Typography>
              <Typography level="body-sm" sx={{ mb: 2, color: 'neutral.700' }}>
                • Run at specified intervals (in seconds)<br />
                • Independent of task execution<br />
                • Ideal for maintenance, cleanup, or periodic reporting<br />
                • Requires interval setting
              </Typography>
            </Box>
            <Typography level="title-sm" sx={{ color: 'primary.500', fontWeight: 'bold', mt: 2 }}>
              Best Practices
            </Typography>
            <Box sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'primary.200' }}>
              <Typography level="body-sm" sx={{ color: 'neutral.700' }}>
                • Always include error handling with try/except blocks<br />
                • Use print statements for logging (automatically captured)<br />
                • Set appropriate timeouts for external API calls<br />
                • Keep functions focused on a single responsibility<br />
              </Typography>
            </Box>
          </Stack>
        </TabPanel>
        {/* Post-Complete Examples Tab */}
        <TabPanel value={1}>
          <Stack spacing={2}>
            <Typography level="title-sm" sx={{ color: 'primary.500', fontWeight: 'bold' }}>
              Post-Complete Function Examples
            </Typography>
            <Typography level="body-sm" sx={{ mb: 2, color: 'neutral.600' }}>
              These functions run after a task is completed and have access to the task result and payload.
            </Typography>
            <Accordion defaultExpanded>
              <AccordionSummary>
                <Typography level="body-md" sx={{ fontWeight: 'bold' }}>Example 1: Send notification for high compliance score</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Card variant="outlined" sx={{ borderColor: '#b1b1b7', height: '200px', position: 'relative', fontSize: '10px' }}>
                  <CodeMirror
                    value={`# Example 1: Send notification for high compliance score\nif result.output.get('complianceScore', 0) > 90:\n    notification_data = {\n        'task_id': task.id,\n        'score': result.output.get('complianceScore'),\n        'status': result.output.get('status'),\n        'timestamp': datetime.now().isoformat()\n    }\n    try:\n        response = requests.post(\n            url='https://api.notification-service.com/send',\n            json=notification_data,\n            headers={'Authorization': 'Bearer your-token'},\n            timeout=10\n        )\n        response.raise_for_status()\n        print(f"Notification sent successfully")\n    except Exception as e:\n        print(f"Failed to send notification: {str(e)}")`}
                    height="200px"
                    extensions={[python()]}
                    basicSetup={{ lineNumbers: true, readOnly: true }}
                    editable={false}
                  />
                </Card>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary>
                <Typography level="body-md" sx={{ fontWeight: 'bold' }}>Example 2: Update external system with task results</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Card variant="outlined" sx={{ borderColor: '#b1b1b7', height: '200px', position: 'relative', fontSize: '10px' }}>
                  <CodeMirror
                    value={`# Example 2: Update external system with task results\ntry:\n    update_data = {\n        'document_id': task.payload.get('document_id'),\n        'compliance_status': result.output.get('status'),\n        'score': result.output.get('complianceScore'),\n        'processed_at': datetime.now().isoformat()\n    }\n    response = requests.put(\n        url='https://api.external-system.com/update',\n        json=update_data,\n        headers={'Authorization': 'Bearer your-token'},\n        timeout=10\n    )\n    response.raise_for_status()\n    print(f"External system updated successfully")\nexcept Exception as e:\n    print(f"Failed to update external system: {str(e)}")`}
                    height="200px"
                    extensions={[python()]}
                    basicSetup={{ lineNumbers: true, readOnly: true }}
                    editable={false}
                  />
                </Card>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary>
                <Typography level="body-md" sx={{ fontWeight: 'bold' }}>Example 3: Process and transform task results</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Card variant="outlined" sx={{ borderColor: '#b1b1b7', height: '200px', position: 'relative', fontSize: '10px' }}>
                  <CodeMirror
                    value={`# Example 3: Process and transform task results\ntry:\n    # Get the compliance data\n    compliance_data = result.output\n    \n    # Validate the data\n    if not compliance_data.get('complianceScore'):\n        error = "Missing compliance score in result"\n        return\n        \n    # Transform the data\n    transformed_data = {\n        'task_id': task.id,\n        'document_id': task.payload.get('document_id'),\n        'compliance_score': compliance_data.get('complianceScore', 0),\n        'status': compliance_data.get('status', 'unknown'),\n        'fields': compliance_data.get('documentFields', {}),\n        'processed_at': datetime.now().isoformat()\n    }\n    \n    # Store the transformed data\n    output = transformed_data\n    print(f"Processed data: {transformed_data}")\nexcept Exception as e:\n    error = f"Error processing data: {str(e)}"`}
                    height="200px"
                    extensions={[python()]}
                    basicSetup={{ lineNumbers: true, readOnly: true }}
                    editable={false}
                  />
                </Card>
              </AccordionDetails>
            </Accordion>
          </Stack>
        </TabPanel>
        {/* Scheduled Examples Tab */}
        <TabPanel value={2}>
          <Stack spacing={2}>
            <Typography level="title-sm" sx={{ color: 'primary.500', fontWeight: 'bold' }}>
              Scheduled Function Examples
            </Typography>
            <Typography level="body-sm" sx={{ mb: 2, color: 'neutral.600' }}>
              These functions run at specified intervals and can perform periodic tasks.
            </Typography>
            <Accordion defaultExpanded>
              <AccordionSummary>
                <Typography level="body-md" sx={{ fontWeight: 'bold' }}>Example 1: Clean up old task results</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Card variant="outlined" sx={{ borderColor: '#b1b1b7', height: '300px', position: 'relative', fontSize: '10px' }}>
                  <CodeMirror
                    value={`# Example 1: Clean up old task results\ntry:\n    # Calculate date threshold (30 days ago)\n    threshold_date = datetime.now() - timedelta(days=30)\n    \n    # Query for old task results\n    old_results = TaskResult.objects.filter(\n        created_at__lt=threshold_date,\n        status='completed'\n    )\n    \n    # Process and archive old results\n    for result in old_results:\n        archive_data = {\n            'task_id': result.task.id,\n            'compliance_score': result.output.get('complianceScore'),\n            'status': result.output.get('status'),\n            'archived_at': datetime.now().isoformat()\n        }\n        \n        try:\n            response = requests.post(\n                url='https://api.archive-system.com/store',\n                json=archive_data,\n                headers={'Authorization': 'Bearer your-token'},\n                timeout=10\n            )\n            response.raise_for_status();\n            result.delete();\n            print(f"Archived and deleted old result {result.id}")\n        except Exception as e:\n            print(f"Failed to archive result {result.id}: {str(e)}")\n            \n    print(f"Cleanup completed. Processed {old_results.count()} results")\nexcept Exception as e:\n    print(f"Error during cleanup: {str(e)}")`}
                    height="300px"
                    extensions={[python()]}
                    basicSetup={{ lineNumbers: true, readOnly: true }}
                    editable={false}
                  />
                </Card>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary>
                <Typography level="body-md" sx={{ fontWeight: 'bold' }}>Example 2: Generate daily compliance report</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Card variant="outlined" sx={{ borderColor: '#b1b1b7', height: '300px', position: 'relative', fontSize: '10px' }}>
                  <CodeMirror
                    value={`# Example 2: Generate daily compliance report\ntry:\n    # Get tasks from last 24 hours\n    start_time = datetime.now() - timedelta(hours=24)\n    recent_tasks = TaskResult.objects.filter(\n        created_at__gte=start_time,\n        status='completed'\n    )\n    \n    # Generate report data\n    report_data = {\n        'period': '24h',\n        'total_tasks': recent_tasks.count(),\n        'avg_compliance_score': sum(\n            task.output.get('complianceScore', 0) \n            for task in recent_tasks\n        ) / max(recent_tasks.count(), 1),\n        'status_breakdown': {\n            'passed': recent_tasks.filter(output__status='passed').count(),\n            'failed': recent_tasks.filter(output__status='failed').count(),\n            'warning': recent_tasks.filter(output__status='warning').count()\n        },\n        'generated_at': datetime.now().isoformat()\n    }\n    \n    # Send report\n    response = requests.post(\n        url='https://api.reporting-system.com/store',\n        json=report_data,\n        headers={'Authorization': 'Bearer your-token'},\n        timeout=10\n    )\n    response.raise_for_status()\n    print(f"Report generated and sent successfully")\nexcept Exception as e:\n    print(f"Failed to generate report: {str(e)}")`}
                    height="300px"
                    extensions={[python()]}
                    basicSetup={{ lineNumbers: true, readOnly: true }}
                    editable={false}
                  />
                </Card>
              </AccordionDetails>
            </Accordion>
          </Stack>
        </TabPanel>
        {/* Context Objects Tab */}
        <TabPanel value={3}>
          <Stack spacing={2}>
            <Typography level="title-sm" sx={{ color: 'primary.500', fontWeight: 'bold' }}>
              Available Context Objects
            </Typography>
            <Typography level="body-sm" sx={{ mb: 2, color: 'neutral.600' }}>
              These objects are available in your function code. Their availability depends on the function type.
            </Typography>
            <Accordion>
              <AccordionSummary>
                <Typography level="title-sm">result Object</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography level="body-sm" sx={{ mb: 1 }}>
                  Available in post-complete functions. Contains the task result data.
                </Typography>
                <Box sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'primary.200' }}>
                  <Typography level="body-sm" sx={{ color: 'neutral.700', mb: 0.5 }}>
                    • <b>result.output</b>: Dictionary containing task output data<br />
                    • <b>result.task</b>: Reference to the associated task<br />
                  </Typography>
                </Box>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary>
                <Typography level="title-sm">task Object</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography level="body-sm" sx={{ mb: 1 }}>
                  Available in post-complete functions. Contains task information.
                </Typography>
                <Box sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'primary.200' }}>
                  <Typography level="body-sm" sx={{ color: 'neutral.700', mb: 0.5 }}>
                    • <b>task.id</b>: Unique identifier of the task<br />
                    • <b>task.status</b>: Current status of the task<br />
                    • <b>task.payload</b>: Dictionary containing task input data<br />
                    • <b>task.events</b>: List of task events
                  </Typography>
                </Box>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary>
                <Typography level="title-sm">Utility Objects</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography level="body-sm" sx={{ mb: 1 }}>
                  Available in all function types. Common utilities for your code.
                </Typography>
                <Box sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'primary.200' }}>
                  <Typography level="body-sm" sx={{ color: 'neutral.700', mb: 0.5 }}>
                    • <b>requests</b>: HTTP request utilities for API calls<br />
                    • <b>datetime</b>: Date and time utilities<br />
                    • <b>output</b>: Variable to store output data from function execution<br />
                    • <b>error</b>: Set this to trigger an error in the function execution
                  </Typography>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Stack>
        </TabPanel>
      </Tabs>
    </Box>
  </Stack>
);

export default FunctionDocumentation; 